/**
 * HOME — one composer, and the work as a board (owner-accepted design,
 * 2026-10-09: "an operating system for professional work, not a
 * reconciliation tool", calm in the way Ramp and Stripe are calm).
 *
 * NOT A DASHBOARD. No chart, no analytics, no activity feed, no counters of
 * things nobody asked about. One place to say what the work is, and the work
 * already in flight laid out by the state it is actually in.
 *
 * THE COLUMNS ARE THE SERVER'S. Their order, their labels, their hints and
 * which work falls in which one are all decided in `@leveri/work-os` and sent
 * already grouped — an empty group never arrives, because a board of empty
 * rails is noise. This screen renders `groups[]` in the order it was given and
 * does not know how many of them exist or what they are called.
 *
 * THE CHAT BOX ASKS NOTHING IT CAN READ (owner, 2026-10-10: "why do I have to
 * choose client and engagement under the chat box?"). One field: say the work,
 * attach the files. Who and when are read from the words; what is not read is
 * asked in the thread, with one-tap answers (`chat.tsx`). The kind of work is
 * read the same way (`workKindFromWords`), never chosen from a list.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { WorkBoardResponse, type WorkBoardResponse as Board, type WorkSummary } from "@leveri/contracts";
import { BOARD_CARD, BoardColumn, BoardGrid, Notice, Reading, SERIF_OPSZ, StateText, markerTone } from "../file/index.js";
import { call, failureMessage } from "../api.js";
import { Desk, usePrincipal } from "./shell.js";
import { ChatBox } from "./chat.js";

export function BoardScreen() {
  const [board, setBoard] = useState<Board | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  /** "Assigned to me": a SELECTION of the server's rows by their holder — no status, label or group is recomputed. */
  const [mine, setMine] = useState(false);
  const me = usePrincipal();

  const load = useCallback(async () => {
    setFailure(null);
    try {
      setBoard(await call("GET", "/api/work", WorkBoardResponse));
    } catch (error) {
      setFailure(failureMessage(error));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const shown = useMemo(() => {
    if (!board || !mine) return board;
    const groups = board.groups.map((g) => ({ ...g, work: g.work.filter((w) => w.assignee?.userId === me?.userId) })).filter((g) => g.work.length > 0);
    return { groups, total: groups.reduce((n, g) => n + g.work.length, 0) };
  }, [board, mine, me?.userId]);

  return (
    <Desk current="work" measure="wide">
      {/* THE ASK. Centred, because it is the one thing this page is for. */}
      <section aria-labelledby="ask" className="mx-auto w-full max-w-[820px] lg:pt-4">
        <h1
          id="ask"
          className="text-center font-serif text-[30px] font-normal leading-[1.15] tracking-[-0.015em] text-ink [text-wrap:balance] sm:text-[34px]"
          style={SERIF_OPSZ}
        >
          What should Leveri work on?
        </h1>
        {/* THE CHAT BOX: say the work, attach the files; Leveri reads who and when and asks only what it cannot read. */}
        <div className="mt-6"><ChatBox board={board} /></div>
      </section>

      {/* THE WORK, AS A BOARD. */}
      <section aria-labelledby="your-work" className="mt-14">
        <div className="flex items-baseline gap-3">
          <h2 id="your-work" className="text-[15px] font-semibold text-ink">Your work</h2>
          {shown && shown.total > 0 && <span className="font-mono text-[13px] text-stone">{shown.total}</span>}
          <span className="flex-1" />
          {board && board.total > 0 && (
            <span className="flex gap-3 text-[13px]" role="group" aria-label="Whose work">
              {([false, true] as const).map((value) => (
                <button key={String(value)} type="button" aria-pressed={mine === value} onClick={() => setMine(value)} data-testid={value ? "board-mine" : "board-everyone"}
                  className={mine === value ? "font-medium text-ink" : "text-stone hover:text-ink"}>
                  {value ? "Assigned to me" : "Everyone's"}
                </button>
              ))}
            </span>
          )}
        </div>

        {failure && (
          <div className="mt-4">
            <Notice>{failure}</Notice>
            <button type="button" onClick={() => void load()} className="mt-2 text-[13px] font-medium text-ink underline-offset-4 hover:underline">
              Try again
            </button>
          </div>
        )}

        {!board && !failure && <div className="mt-4"><Reading>Loading the work…</Reading></div>}

        {board && mine && board.groups.length > 0 && shown && shown.groups.length === 0 && (
          <p className="mt-4 border-t border-rule pt-6 text-sm text-stone" data-testid="board-mine-empty">Nothing is assigned to you. Work is given to a person from its file.</p>
        )}

        {board && board.groups.length === 0 && (
          <div className="mt-4 border-t border-rule pt-8 text-center">
            <p className="text-[15px] font-medium text-ink">Nothing in flight yet</p>
            <p className="mx-auto mt-1.5 max-w-[460px] text-sm text-stone">
              Say what the work is above and Leveri will open a file for it. It will appear here, grouped by where it stands.
            </p>
          </div>
        )}

        {shown && shown.groups.length > 0 && (
          <BoardGrid className="mt-5">
            {shown.groups.map((group) => (
              <BoardColumn key={group.key} id={`group-${group.key}`} label={group.label} count={group.work.length} hint={group.hint}>
                {group.work.map((work) => <WorkCard key={work.id} work={work} />)}
              </BoardColumn>
            ))}
          </BoardGrid>
        )}
      </section>
    </Desk>
  );
}

/**
 * One piece of work on the board.
 *
 * Every word and every number here is the projection's: the title the
 * accountant wrote, the client and engagement the work is booked to, the
 * period as the engagement states it, and the status word with the tone the
 * server decided. Nothing is re-phrased and nothing is counted again. A long
 * title or name is clipped on the card and whole in the file it opens.
 */
function WorkCard({ work }: { work: WorkSummary }) {
  return (
    <li>
      <Link to={`/work/${work.id}`} className={BOARD_CARD}>
        <span className="line-clamp-3 block text-sm font-medium leading-snug text-ink [overflow-wrap:anywhere]" title={work.title}>
          {work.title}
        </span>
        <span className="mt-1 block truncate text-[12px] text-stone" title={`${work.clientName} · ${work.engagementTitle}`}>
          {work.clientName} · {work.engagementTitle}
        </span>
        {/* The period as one phrase, read for a person on the server. The
            machine values stay on the wire and sit in the title. */}
        <span className="block text-[12px] text-stone" title={`${work.periodStart} – ${work.cutOff}`}>
          {work.periodText} · {work.currency}
        </span>
        <span className="mt-2.5 flex">
          <StateText tone={markerTone(work.tone)} className="text-[13px]">{work.statusLabel}</StateText>
        </span>
        {/* Counts as labelled figures rather than sentences: the server sent
            numbers, and a sentence built around one would be this screen
            stating something the projection did not. */}
        <span className="mt-1 block text-[12px] text-stone">
          <Count label="Records">{work.recordCount}</Count> · <Count label="Open requests">{work.openRequestCount}</Count>
          {work.workpaperVersion !== null && <> · <Count label="Workpaper">v{work.workpaperVersion}</Count></>}
        </span>
        {work.assignee && <span className="mt-1 block truncate text-[12px] text-ink-soft">Held by {work.assignee.displayName}</span>}
      </Link>
    </li>
  );
}

/** A labelled number, kept on one line, the number in the figure face. */
function Count({ label, children }: { label: string; children: ReactNode }) {
  return <span className="whitespace-nowrap">{label} <span className="font-mono text-ink-soft">{children}</span></span>;
}
